import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../components/admin/common/NotiService';
import LoadingScreen from '../../../components/admin/common/OZLoadingScreen/OZLoadingScreen';
import {
    createOffer as createOfferApi,
    getOfferByFlowAndName,
    getOotdFlows,
    getOffersByFlowId,
    processOfferAudioFile,
    updateOffer as updateOfferApi,
} from '../../../services/offer-language-configuration-service';
import FlowSelector from './components/FlowSelector';
import LanguageSidebar from './components/LanguageSidebar';
import OfferMetadataForm from './components/OfferMetadataForm';
import OfferSelector from './components/OfferSelector';
import VariantEditor from './components/VariantEditor';
import './offerLanguageConfiguration.css';
import { IvrFlow, OfferAudioUploadState, OfferConfig, PageMode } from './types';
import {
    buildSavePayload,
    cloneData,
    createEmptyOffer,
    createEmptyVariant,
    deriveIsActive,
    getDateTimeValue,
    getOfferPermissions,
    getVariantStateKey,
    hasOfferRequiredFieldErrors,
    isOfferDirty,
    withMandatoryLanguages,
} from './utils';

type UploadStateMap = Record<string, OfferAudioUploadState>;

export default function OfferLanguageConfiguration() {
    const { hasBaseAccess, canEdit } = getOfferPermissions();

    const [pageMode, setPageMode] = useState<PageMode>('list');
    const [flows, setFlows] = useState<IvrFlow[]>([]);
    const [selectedFlow, setSelectedFlow] = useState<IvrFlow | null>(null);
    const [offerList, setOfferList] = useState<OfferConfig[]>([]);
    const [offer, setOffer] = useState<OfferConfig | null>(null);
    const [snapshotOffer, setSnapshotOffer] = useState<OfferConfig | null>(null);
    const [selectedLanguageIndex, setSelectedLanguageIndex] = useState(0);
    const [languageSearchText, setLanguageSearchText] = useState('');
    const [uploadStateMap, setUploadStateMap] = useState<UploadStateMap>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const previewUrlsRef = useRef<Record<string, string>>({});

    // Load flows on mount
    useEffect(() => {
        const loadFlows = async () => {
            setLoading(true);
            try {
                const flowList = await getOotdFlows();
                if (flowList.length > 0) {
                    setFlows(flowList);
                } else {
                    showErrorNotification('No IVR flows found.');
                    setFlows([]);
                }
            } catch {
                showErrorNotification('Failed to load IVR flows.');
                setFlows([]);
            } finally {
                setLoading(false);
            }
        };
        loadFlows();
    }, []);

    // Cleanup preview URLs
    useEffect(() => {
        return () => {
            Object.values(previewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    const hasPendingUploads = Object.values(uploadStateMap).some((s) => s.status === 'uploading');
    const hasUnsavedChanges = offer ? isOfferDirty(offer, snapshotOffer) : false;
    const hasErrors = offer ? hasOfferRequiredFieldErrors(offer) : false;
    const selectedLanguage = offer && offer.languages[selectedLanguageIndex] ? offer.languages[selectedLanguageIndex] : null;

    const clearUploadState = () => {
        Object.values(previewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
        previewUrlsRef.current = {};
        setUploadStateMap({});
    };

    // --- Flow & Offer selection handlers ---

    const handleFlowChange = async (flow: IvrFlow | null) => {
        setSelectedFlow(flow);
        setOffer(null);
        setSnapshotOffer(null);
        setOfferList([]);
        setPageMode('list');
        setSelectedLanguageIndex(0);
        setLanguageSearchText('');
        clearUploadState();

        if (!flow) return;

        setLoading(true);
        try {
            const offers = await getOffersByFlowId(flow.flowId);
            const withStatus = offers.map((o) => ({
                ...withMandatoryLanguages(o),
                is_active: o.is_active ?? deriveIsActive(o.valid_from, o.valid_till, o.valid_from_time, o.valid_till_time),
            }));
            setOfferList(withStatus);
        } catch {
            showErrorNotification('Failed to load offers for this flow.');
            setOfferList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOfferSelect = async (selected: OfferConfig | null) => {
        if (!selected || !selectedFlow) return;

        setLoading(true);
        try {
            const selectedOffer = await getOfferByFlowAndName(
                selected.flow_id || selectedFlow.flowId,
                selected.offer_name
            );
            const working = withMandatoryLanguages(cloneData(selectedOffer));
            working.is_active = working.is_active ?? deriveIsActive(
                working.valid_from,
                working.valid_till,
                working.valid_from_time,
                working.valid_till_time
            );
            setOffer(working);
            setSnapshotOffer(cloneData(working));
            setPageMode('edit');
            setSelectedLanguageIndex(0);
            setLanguageSearchText('');
            clearUploadState();
        } catch {
            showErrorNotification('Failed to load selected offer details.');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateOffer = () => {
        if (!selectedFlow) {
            showInfoNotification('Select an IVR Flow first.');
            return;
        }
        const empty = createEmptyOffer(selectedFlow.flowId);
        setOffer(empty);
        setSnapshotOffer(null);
        setPageMode('create');
        setSelectedLanguageIndex(0);
        setLanguageSearchText('');
        clearUploadState();
    };

    const handleBack = () => {
        if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Discard and go back?')) return;
        setOffer(null);
        setSnapshotOffer(null);
        setPageMode('list');
        setSelectedLanguageIndex(0);
        setLanguageSearchText('');
        clearUploadState();
    };

    // --- Offer field handlers ---

    const handleFieldChange = (field: keyof OfferConfig, value: string) => {
        if (!offer) return;
        setOffer((prev) => {
            if (!prev) return prev;
            const updated = { ...prev, [field]: value };
            updated.is_active = deriveIsActive(
                updated.valid_from,
                updated.valid_till,
                updated.valid_from_time,
                updated.valid_till_time
            );
            return updated;
        });
    };

    // --- Variant handlers ---

    const handleAddVariant = () => {
        if (!offer || !selectedLanguage) return;
        const newVariant = createEmptyVariant('');
        setOffer((prev) => {
            if (!prev) return prev;
            const languages = [...prev.languages];
            languages[selectedLanguageIndex] = {
                ...languages[selectedLanguageIndex],
                variants: [...languages[selectedLanguageIndex].variants, newVariant],
            };
            return { ...prev, languages };
        });
    };

    const handleRemoveVariant = (variantIndex: number) => {
        if (!offer) return;
        setOffer((prev) => {
            if (!prev) return prev;
            const languages = [...prev.languages];
            const variants = [...languages[selectedLanguageIndex].variants];
            variants.splice(variantIndex, 1);
            languages[selectedLanguageIndex] = { ...languages[selectedLanguageIndex], variants };
            return { ...prev, languages };
        });
    };

    const handleBoxNameChange = (variantIndex: number, value: string) => {
        if (!offer) return;
        setOffer((prev) => {
            if (!prev) return prev;
            const languages = [...prev.languages];
            const variants = [...languages[selectedLanguageIndex].variants];
            variants[variantIndex] = { ...variants[variantIndex], box_name: value };
            languages[selectedLanguageIndex] = { ...languages[selectedLanguageIndex], variants };
            return { ...prev, languages };
        });
    };

    const handlePackageIdChange = (variantIndex: number, value: string) => {
        if (!offer) return;
        setOffer((prev) => {
            if (!prev) return prev;
            const languages = [...prev.languages];
            const variants = [...languages[selectedLanguageIndex].variants];
            variants[variantIndex] = { ...variants[variantIndex], package_id: value };
            languages[selectedLanguageIndex] = { ...languages[selectedLanguageIndex], variants };
            return { ...prev, languages };
        });
    };

    // --- Audio upload ---

    const handleAudioFileUpload = async (variantIndex: number, file: File) => {
        if (!offer || !selectedLanguage) return;
        const variant = selectedLanguage.variants[variantIndex];
        const stateKey = getVariantStateKey(offer.offer_name, selectedLanguage.language_name, variant.id);

        setUploadStateMap((prev) => ({ ...prev, [stateKey]: { status: 'uploading', fileName: file.name } }));

        try {
            const response = await processOfferAudioFile({
                offerKey: offer.offer_name,
                flowId: offer.flow_id,
                language: selectedLanguage.language_name,
                variant: variant.box_name,
                brand: offer.brand,
                file,
            });

            const previewUrl = URL.createObjectURL(file);
            const oldUrl = previewUrlsRef.current[stateKey];
            if (oldUrl) URL.revokeObjectURL(oldUrl);
            previewUrlsRef.current[stateKey] = previewUrl;

            setOffer((prev) => {
                if (!prev) return prev;
                const languages = [...prev.languages];
                const variants = [...languages[selectedLanguageIndex].variants];
                variants[variantIndex] = {
                    ...variants[variantIndex],
                    audio_file: response.fileName,
                    audioUrl: response.audioUrl,
                };
                languages[selectedLanguageIndex] = { ...languages[selectedLanguageIndex], variants };
                return { ...prev, languages };
            });

            setUploadStateMap((prev) => ({
                ...prev,
                [stateKey]: { status: 'success', fileName: response.fileName, processedAudioUrl: response.audioUrl, previewUrl },
            }));
            showSuccessNotification(`${variant.box_name || 'Box'} audio uploaded for ${selectedLanguage.language_name}.`);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Audio upload failed.';
            setUploadStateMap((prev) => ({ ...prev, [stateKey]: { status: 'error', error: message, fileName: file.name } }));
            showErrorNotification(message);
        }
    };

    // --- Save / Reset ---

    const handleReset = () => {
        if (hasPendingUploads) {
            showInfoNotification('Wait for active audio uploads to finish before resetting.');
            return;
        }
        if (!snapshotOffer) return;
        setOffer(cloneData(snapshotOffer));
        setSelectedLanguageIndex(0);
        clearUploadState();
        showInfoNotification('Changes were reset to the last saved state.');
    };

    const handleSave = async () => {
        if (!offer || !hasBaseAccess || !canEdit) return;

        if (hasPendingUploads) {
            showErrorNotification('Finish all audio uploads before saving.');
            return;
        }

        if (offer.offer_name.trim().length === 0) {
            showErrorNotification('Offer name is required.');
            return;
        }

        if (!offer.valid_from || !offer.valid_from_time || !offer.valid_till || !offer.valid_till_time) {
            showErrorNotification('Validity date and time values are required.');
            return;
        }

        const validFromDateTime = getDateTimeValue(offer.valid_from, offer.valid_from_time);
        const validTillDateTime = getDateTimeValue(offer.valid_till, offer.valid_till_time);
        const validFromTs = new Date(validFromDateTime).getTime();
        const validTillTs = new Date(validTillDateTime).getTime();

        if (Number.isNaN(validFromTs) || Number.isNaN(validTillTs)) {
            showErrorNotification('Please provide valid date and time values.');
            return;
        }

        if (validFromTs > validTillTs) {
            showErrorNotification('"Valid Till" must be greater than or equal to "Valid From".');
            return;
        }

        if (hasErrors) {
            showErrorNotification('All language fields are mandatory: each variant must include name, Package ID, and Audio URL.');
            return;
        }

        if (pageMode === 'edit' && !hasUnsavedChanges) {
            showInfoNotification('No changes to save.');
            return;
        }

        setSaving(true);
        try {
            const payload = buildSavePayload(offer);
            const apiFn = pageMode === 'create' ? createOfferApi : updateOfferApi;
            const response = await apiFn(payload);

            if (response.Status === 'Success') {
                const savedOffer = withMandatoryLanguages(cloneData(response.Data));
                savedOffer.is_active = savedOffer.is_active ?? deriveIsActive(
                    savedOffer.valid_from,
                    savedOffer.valid_till,
                    savedOffer.valid_from_time,
                    savedOffer.valid_till_time
                );
                setOffer(savedOffer);
                setSnapshotOffer(cloneData(savedOffer));
                clearUploadState();
                showSuccessNotification(response.Message);

                if (pageMode === 'create') {
                    setPageMode('edit');
                    // Refresh offer list
                    if (selectedFlow) {
                        const offers = await getOffersByFlowId(selectedFlow.flowId);
                        setOfferList(
                            offers.map((o) => ({
                                ...withMandatoryLanguages(o),
                                is_active: o.is_active ?? deriveIsActive(
                                    o.valid_from,
                                    o.valid_till,
                                    o.valid_from_time,
                                    o.valid_till_time
                                ),
                            }))
                        );
                    }
                }
            } else {
                showErrorNotification(response.Message || 'Failed to save offer.');
            }
        } catch {
            showErrorNotification('Failed to save offer.');
        } finally {
            setSaving(false);
        }
    };

    // --- Render ---

    if (loading) {
        return (
            <div style={{ position: 'fixed', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                <LoadingScreen />
            </div>
        );
    }

    const isFormMode = pageMode === 'edit' || pageMode === 'create';

    return (
        <div className="oz-offer-language-config-page">
            {(saving || hasPendingUploads) && (
                <div style={{ position: 'fixed', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backgroundColor: 'rgba(255, 255, 255, 0.45)' }}>
                    <LoadingScreen />
                </div>
            )}

            <Typography className="oz-page-header">Configurations</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Typography className="oz-page-heading">
                    {pageMode === 'create' ? 'Create New Offer' : 'Offer and Language Configuration'}
                </Typography>
                <div className="oz-offer-language-config-actions">
                    {isFormMode && (
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ borderRadius: '10px', height: '32px' }}
                        >
                            Back
                        </Button>
                    )}

                    {isFormMode && offer && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                            
                            {hasUnsavedChanges && <Chip
                                sx={{ borderRadius: '10px', height: '32px' }}
                                label="Unsaved changes" color="warning" />}
                        </Stack>
                    )}

                    {pageMode === 'edit' && (
                        <Button
                            variant="outlined"
                            startIcon={<RestartAltOutlinedIcon />}
                            onClick={handleReset}
                            disabled={!hasUnsavedChanges || saving}
                            sx={{ borderRadius: '10px', height: '32px' }}
                        >
                            Reset
                        </Button>
                    )}

                    {isFormMode && (
                        <Button
                            variant="contained"
                            startIcon={<SaveOutlinedIcon />}
                            onClick={handleSave}
                            disabled={!canEdit || saving || hasPendingUploads || (pageMode === 'edit' && !hasUnsavedChanges)}
                            sx={{ borderRadius: '10px', height: '32px' }}
                        >
                            {pageMode === 'create' ? 'Save Offer' : 'Save Changes'}
                        </Button>
                    )}

                    {pageMode === 'list' && canEdit && (
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={handleCreateOffer}
                            disabled={!selectedFlow}
                            sx={{ borderRadius: '10px', height: '32px' }}
                        >
                            Create Offer
                        </Button>
                    )}
                </div>
            </Stack>

            {/* Flow & Offer Selection Panel */}
            <Box className="oz-offer-language-config-panel oz-offer-language-config-offer-panel">
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <FlowSelector
                            flows={flows}
                            selectedFlow={selectedFlow}
                            onFlowChange={handleFlowChange}
                            disabled={isFormMode}
                        />
                    </Box>
                    {pageMode === 'list' && selectedFlow && offerList.length > 0 && (
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <OfferSelector
                                offers={offerList}
                                selectedOffer={null}
                                onOfferSelect={handleOfferSelect}
                            />
                        </Box>
                    )}
                </Stack>

                {pageMode === 'list' && selectedFlow && offerList.length === 0 && !loading && (
                    <Typography sx={{ color: '#64748b', fontSize: '13px', marginTop: '12px', textAlign: 'center' }}>
                        No offers found for this flow.
                    </Typography>
                )}
            </Box>

            {/* Edit/Create Form */}
            {isFormMode && offer && (
                <>
                    <Box className="oz-offer-language-config-panel oz-offer-language-config-offer-panel">
                        <OfferMetadataForm
                            offer={offer}
                            canEdit={canEdit}
                            onFieldChange={handleFieldChange}
                        />
                    </Box>

                    <div className="oz-offer-language-config-layout">
                        <LanguageSidebar
                            offer={offer}
                            snapshotOffer={snapshotOffer}
                            selectedLanguageIndex={selectedLanguageIndex}
                            canEdit={canEdit}
                            searchText={languageSearchText}
                            onSearchChange={setLanguageSearchText}
                            onSelectLanguage={setSelectedLanguageIndex}
                        />

                        <VariantEditor
                            offer={offer}
                            snapshotOffer={snapshotOffer}
                            language={selectedLanguage}
                            languageIndex={selectedLanguageIndex}
                            canEdit={canEdit}
                            uploadStateMap={uploadStateMap}
                            onBoxNameChange={handleBoxNameChange}
                            onPackageIdChange={handlePackageIdChange}
                            onFileUpload={handleAudioFileUpload}
                            onAddVariant={handleAddVariant}
                            onRemoveVariant={handleRemoveVariant}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
